// src/state/AuthContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, serverTimestamp, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [authUser, setAuthUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // UI: checkout confirmation + processing state
  const [checkoutStatus, setCheckoutStatus] = useState("idle"); 
  // "idle" | "processing" | "success" | "failed"

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setAuthUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authUser?.uid) {
      setAppUser(null);
      return;
    }

    const ref = doc(db, "users", authUser.uid);

    (async () => {
      try {
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          await setDoc(
            ref,
            {
              uid: authUser.uid,
              email: authUser.email ?? null,
              created_at: serverTimestamp(),
              last_login: serverTimestamp(),
              pro: false,
            },
            { merge: true }
          );
        } else {
          const existing = snap.data();
          const updates = {
            last_login: serverTimestamp(),
          };

          if ((existing?.email ?? null) !== (authUser.email ?? null)) {
            updates.email = authUser.email ?? null;
          }

          await updateDoc(ref, updates);
        }
      } catch (err) {
        console.error("Failed to init user doc:", err);
      }
    })();

    const unsub = onSnapshot(ref, (snap) => {
      setAppUser(snap.exists() ? snap.data() : null);
    });

    return () => unsub();
  }, [authUser?.uid, authUser?.email]);

  useEffect(() => {
    if (!authUser) return;

    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (checkout !== "success" && checkout !== "cancel") return;

    // If user cancelled checkout
    if (checkout === "cancel") {
      setCheckoutStatus("failed");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    // checkout=success
    let cancelled = false;
    setCheckoutStatus("processing");

    (async () => {
      try {
        // Refresh ID token so custom claims update immediately
        await authUser.getIdToken(true);

        // If Firestore already says pro, we're done
        if (appUser?.pro === true) {
          if (!cancelled) {
            setCheckoutStatus("success");
            window.history.replaceState({}, "", window.location.pathname);
          }
          return;
        }

        // Otherwise, wait up to 20s for Firestore to flip pro -> true
        const started = Date.now();
        const timeoutMs = 20000;

        const interval = setInterval(() => {
          if (cancelled) return clearInterval(interval);

          if (appUser?.pro === true) {
            clearInterval(interval);
            setCheckoutStatus("success");
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }

          if (Date.now() - started > timeoutMs) {
            clearInterval(interval);
            setCheckoutStatus("failed");
            window.history.replaceState({}, "", window.location.pathname);
          }
        }, 500);

      } catch (e) {
        console.error("Checkout confirmation failed:", e);
        if (!cancelled) {
          setCheckoutStatus("failed");
          window.history.replaceState({}, "", window.location.pathname);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // IMPORTANT: we depend on appUser?.pro so it updates quickly when Firestore changes
  }, [authUser, appUser?.pro]);

  const value = useMemo(
    () => ({
      authUser,
      appUser,
      loading,
      isSignedIn: !!authUser?.uid,
      isPro: appUser?.pro === true,
      uid: authUser?.uid ?? null,
      email: authUser?.email ?? null,

      // Expose checkout UI status globally
      checkoutStatus,
      setCheckoutStatus,
    }),
    [authUser, appUser, loading, checkoutStatus]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}